import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import { getInvoiceStatus } from "@/lib/invoice-status";
import type { DBProject, ChecklistItem } from "@/lib/db-types";
import type { CustomerInfo, ProjectOwnerWithUser } from "@/hooks/use-project-detail";
import { EditableInfoRow } from "@/components/shared/EditableInfoRow";
import ProjectStatusBadge from "./ProjectStatusBadge";
import { ProjectProgressBar } from "@/components/shared/ProjectProgressBar";
import type { ProjectProgress } from "@/hooks/use-project-progress";
import type { RecentTask } from "@/hooks/use-recent-completed-tasks";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { SrIcon } from "@/components/ui/SrIcon";
import { findMeeting, useProjectMeetings } from "@/hooks/use-project-meetings";
import PoLinkStatus from "@/components/customers/PoLinkStatus";

interface ProjectHeaderProps {
  project: DBProject;
  customer: Pick<CustomerInfo, "id" | "name" | "poweroffice_customer_id"> | null;
  owners: ProjectOwnerWithUser[];
  invoicedAmount: number;
  invoiceCount: number;
  budgetLines: BudgetLineRow[];
  progress?: ProjectProgress;
  recentTasks?: RecentTask[];
  internalReadOnly: boolean;
  isAdmin: boolean;
  /** VIB-722c: gate "Lukk prosjekt"-menyitem (close_project). */
  canCloseProject?: boolean;
  onStatusChange: (newStatus: string) => void;
  onStartupClick: () => void;
  onStatusMeetingClick: () => void;
  onDebriefClick: () => void;
  onReopenClick?: () => void;
}


export function ProjectHeader({
  project: p,
  customer,
  owners,
  invoicedAmount,
  invoiceCount,
  budgetLines,
  progress,
  recentTasks,
  internalReadOnly,
  isAdmin,
  canCloseProject = true,
  onStatusChange,
  onStartupClick,
  onStatusMeetingClick,
  onDebriefClick,
  onReopenClick,
}: ProjectHeaderProps) {
  // Hent møte-statuser fra project_meetings (erstatter projects-kolonner).
  const { data: meetings } = useProjectMeetings(p.id);
  const startupStatus = findMeeting(meetings, "startup")?.status ?? null;
  const statusMeetingStatus = findMeeting(meetings, "status")?.status ?? null;
  const debriefStatus = findMeeting(meetings, "debrief")?.status ?? null;

  /** Felles stil for møte-chip: tre tilstander (grønn/blå/grå). */
  const meetingChipClasses = (status: string | null) =>
    status === "Gjennomført"
      ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
      : status === "Ikke nødvendig"
      ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
      : "bg-muted text-muted-foreground hover:bg-muted/80";

  const meetingChipIcon = (status: string | null): "check-circle2" | "x-circle" | "circle-dashed" =>
    status === "Gjennomført"
      ? "check-circle2"
      : status === "Ikke nødvendig"
      ? "x-circle"
      : "circle-dashed";


  return (
    <div className="space-y-6">
      {/* Back link + Header + Status in one flex container */}
      <div className="flex items-start justify-between gap-4">
        <Link to="/prosjekter" className="text-muted-foreground hover:text-primary mt-0.5 flex-shrink-0">
          <SrIcon name="arrow-left" className="w-4 h-4"  />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center items-start gap-2 sm:gap-3 mb-1 flex-wrap">
            <span className="text-[12px] font-medium uppercase tracking-[0.06em] text-primary tabular-nums flex-shrink-0">{p.project_code}</span>
            <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] leading-[1.1] break-words">{p.name}</h1>
            <ProjectStatusBadge status={p.project_status} onStatusChange={onStatusChange} isAdmin={isAdmin} />
          </div>
          {customer?.name && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1.5 text-xs text-muted-foreground">
              <span>{customer.name}</span>
              {/* VIB-2575: interne prosjekter faktureres ikke, så PO-kobling er irrelevant der. */}
              {!p.is_internal && (
                <PoLinkStatus
                  customerId={customer.id}
                  poCustomerId={customer.poweroffice_customer_id}
                  projectId={p.id}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Internal project read-only notice — kun for interne prosjekter.
          Lukkede prosjekter får sitt eget lock-banner lenger ned uansett. */}
      {internalReadOnly && p.is_internal && p.project_status !== "completed" && (
        <div className="px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2">
          <SrIcon name="eye" className="w-4 h-4 shrink-0"  />
          <span>Internt prosjekt, kun ledelsen kan tildele timer og redigere.</span>
        </div>
      )}

      {/* Progress bar + Deadline — skjules for internprosjekter */}
      {!p.is_internal && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="min-w-[200px] flex-1 max-w-[800px] space-y-1">
              <ProjectProgressBar progress={progress} showLabel />
              {recentTasks && recentTasks.length > 0 && (
                <p className="text-xs text-muted-foreground truncate">
                  Siste godkjente oppgaver: {recentTasks.map(t => t.description ?? "Uten beskrivelse").join(" · ")}
                </p>
              )}
            </div>

            {/* Deadline inline with progress bar — alltid synlig, viser "—" når ikke satt */}
            <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
              <SrIcon name="clock" className="w-3.5 h-3.5 text-muted-foreground"  />
              <EditableInfoRow
                label="Est. slutt faktura"
                value={p.estimated_deadline}
                field="estimated_deadline"
                projectId={p.id}
                projectUpdatedAt={p.updated_at}
                type="date"
              />
            </div>
          </div>

          {/* Invoice status + Meeting badges + Quick Actions */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Financial: Invoice status */}
            {(() => {
              const contractTotal = Number(p.total_amount ?? 0);
              const invStatus = getInvoiceStatus(invoicedAmount, contractTotal);
              const pct = contractTotal > 0 ? Math.round((invoicedAmount / contractTotal) * 100) : 0;
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
                        invStatus.bg,
                        invStatus.text
                      )}
                    >
                      {invStatus.icon === "full" && <SrIcon name="check-circle2" className="w-3 h-3"  />}
                      {invStatus.icon === "partial" && <SrIcon name="receipt" className="w-3 h-3"  />}
                      {invStatus.icon === "none" && <SrIcon name="circle-dashed" className="w-3 h-3"  />}
                      <span className="hidden sm:inline whitespace-nowrap">{invStatus.label}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">
                    {invStatus.label}: {formatNOK(invoicedAmount)} av {formatNOK(contractTotal)} ({pct}%)
                  </TooltipContent>
                </Tooltip>
              );
            })()}

            <button
              onClick={onStartupClick}
              className={cn(
                "hidden xl:flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
                meetingChipClasses(startupStatus),
              )}
            >
              <SrIcon name={meetingChipIcon(startupStatus)} className="w-3 h-3" />
              <span>Oppstart</span>
            </button>

            <button
              onClick={onStatusMeetingClick}
              className={cn(
                "hidden xl:flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
                meetingChipClasses(statusMeetingStatus),
              )}
            >
              <SrIcon name={meetingChipIcon(statusMeetingStatus)} className="w-3 h-3" />
              <span>Statusmøte</span>
            </button>

            <button
              onClick={onDebriefClick}
              className={cn(
                "hidden xl:flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
                meetingChipClasses(debriefStatus),
              )}
            >
              <SrIcon name={meetingChipIcon(debriefStatus)} className="w-3 h-3" />
              <span>Debrief</span>
            </button>

            {/* Quick Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8" aria-label="Prosjekthandlinger">
                  <SrIcon name="more-vertical" className="w-4 h-4"  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem asChild>
                  <Link to="/mine-oppgaver" className="flex items-center gap-2 cursor-pointer">
                    <SrIcon name="clock" className="w-4 h-4"  />
                    <span>Logg timer</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onStartupClick} className="xl:hidden">
                  <SrIcon name="check-circle2" className="w-4 h-4 mr-2"  />
                  <span>Oppstartsmøte</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onStatusMeetingClick} className="xl:hidden">
                  <SrIcon name="check-circle2" className="w-4 h-4 mr-2"  />
                  <span>Statusmøte</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDebriefClick} className="xl:hidden">
                  <SrIcon name="check-circle2" className="w-4 h-4 mr-2"  />
                  <span>Debrief</span>
                </DropdownMenuItem>
                <DropdownMenuItem asChild disabled={p.project_status === "completed"}>
                  <Link to={`/prosjekter/${p.id}/okonomi?seksjon=faktura`} className="flex items-center gap-2 cursor-pointer">
                    <SrIcon name="file-text" className="w-4 h-4"  />
                    <span>Opprett faktura</span>
                  </Link>
                </DropdownMenuItem>
                {canCloseProject && (
                  p.project_status === "completed" ? (
                    <DropdownMenuItem onClick={onReopenClick}>
                      <SrIcon name="lock-open" className="w-4 h-4 mr-2" />
                      <span>Åpne på nytt</span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => onStatusChange("completed")}>
                      <SrIcon name="check-circle" className="w-4 h-4 mr-2"  />
                      <span>Lukk prosjekt</span>
                    </DropdownMenuItem>
                  )
                )}
                <DropdownMenuItem asChild>
                  <Link to={`/prosjekter/${p.id}/administrasjon`} className="flex items-center gap-2 cursor-pointer">
                    <SrIcon name="settings2" className="w-4 h-4"  />
                    <span>Detaljer</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {/* Quick Actions Dropdown for internprosjekter — forenklet, kun Logg timer + Lukk/Åpne */}
      {p.is_internal && (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" aria-label="Prosjekthandlinger">
                <SrIcon name="more-vertical" className="w-4 h-4"  />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem asChild>
                <Link to="/mine-oppgaver" className="flex items-center gap-2 cursor-pointer">
                  <SrIcon name="clock" className="w-4 h-4"  />
                  <span>Logg timer</span>
                </Link>
              </DropdownMenuItem>
              {canCloseProject && (
                p.project_status === "completed" ? (
                  <DropdownMenuItem onClick={onReopenClick}>
                    <SrIcon name="lock-open" className="w-4 h-4 mr-2" />
                    <span>Åpne på nytt</span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => onStatusChange("completed")}>
                    <SrIcon name="check-circle" className="w-4 h-4 mr-2"  />
                    <span>Lukk prosjekt</span>
                  </DropdownMenuItem>
                )
              )}
              <DropdownMenuItem asChild>
                <Link to={`/prosjekter/${p.id}/administrasjon`} className="flex items-center gap-2 cursor-pointer">
                  <SrIcon name="settings2" className="w-4 h-4"  />
                  <span>Detaljer</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Lock banner: completed project */}
      {p.project_status === "completed" && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded border-l-4 border-l-muted-foreground bg-muted/40 text-muted-foreground">
          <SrIcon name="lock" className="w-3.5 h-3.5 shrink-0" />
          <p className="text-xs flex-1">
            Prosjektet er lukket. Endringer blokkeres av systemet — bruk "Åpne på nytt" for å redigere.
          </p>
        </div>
      )}

      {/* Warning: active project without startup invoice — ikke relevant for internprosjekter */}
      {!p.is_internal && p.project_status === "active" && invoiceCount === 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded border-l-4 border-l-amber-400 bg-amber-50/70 text-amber-700">
          <SrIcon name="alert-triangle" className="w-3.5 h-3.5 shrink-0"  />
          <p className="text-xs flex-1">Prosjektet er aktivt, men ingen faktura er opprettet ennå.</p>
          <Link
            to={`/prosjekter/${p.id}/okonomi?seksjon=faktura`}
            className="inline-flex items-center h-6 px-2 rounded text-xs font-medium text-amber-700 hover:bg-amber-100/50 shrink-0"
          >
            Opprett faktura
          </Link>
        </div>
      )}
    </div>
  );
}
